import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, ScanText, Image as ImageIcon, X } from "lucide-react";

const TYPE_STYLES = "px-2.5 py-1 rounded-md text-[11px] font-mono bg-[#F3EEFA] text-[#59288E]";

const DOC_TYPES = [
  "Census", "Birth Certificate", "Death Certificate", "Marriage Record",
  "Enrollment Record", "Land Record", "Freedmen Record", "Newspaper Article",
  "Family Bible", "Photograph", "Military Record", "Church Record", "Other",
];

const ACCEPT = ".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf";
const ALLOWED_EXT = ["jpg", "jpeg", "png", "pdf"];

export default function Vault({ onNavigate }) {
  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ title: "", record_type: "Census" });
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);
  if (!docs) return <Loader />;

  const types = ["All", ...Array.from(new Set(docs.map((d) => d.record_type)))];
  const shown = filter === "All" ? docs : docs.filter((d) => d.record_type === filter);

  const openUpload = () => {
    setForm({ title: "", record_type: "Census" });
    setFile(null);
    setUploadOpen(true);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("Unsupported file", { description: "Please upload a JPG, PNG or PDF." });
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(f);
    if (!form.title) setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, "") }));
  };

  const submitUpload = async () => {
    if (!form.title.trim()) { toast.error("Please enter a title"); return; }
    if (!file) { toast.error("Please choose a JPG, PNG or PDF file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const q = `title=${encodeURIComponent(form.title.trim())}&record_type=${encodeURIComponent(form.record_type)}&evidence_status=UNVERIFIED`;
      const r = await api.post(`/documents/upload?${q}`, fd);
      if (r.data.scanned) toast.success("Document uploaded & auto-read by AI", { description: "Text extracted — open it to review or analyze." });
      else toast.success("Document added to your Ancestral Vault");
      setUploadOpen(false);
      await load();
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="module-vault">
      <PageHeader
        eyebrow="Research • Preserve"
        title="My Ancestral Vault"
        subtitle="Upload census records, certificates, Freedmen records, land patents, family Bibles, photographs and more. Each document carries an evidence status."
        right={
          <button
            data-testid="vault-upload-btn"
            onClick={openUpload}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading ? "Uploading & reading…" : "Upload Document"}
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {types.map((t) => (
          <button
            key={t}
            data-testid={`vault-filter-${t}`}
            onClick={() => setFilter(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${filter === t ? "bg-[#D4AF37] border-[#8F7220] text-[#180D24]" : "bg-white border-[#E4DCD3] text-[#756D7E] hover:border-[#D4AF37]"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((d, i) => (
          <Panel
            key={d.id}
            data-testid={`vault-card-${d.id}`}
            className="p-5 cursor-pointer hover:border-[#D4AF37] hover:shadow-md transition-all hp-fade-up"
            style={{ animationDelay: `${i * 40}ms` }}
            onClick={() => setSelected(d)}
          >
            <div className="flex items-start justify-between mb-3">
              <FileText size={22} className="text-[#59288E]" />
              <EvidenceBadge status={d.evidence_status} />
            </div>
            <div className="font-semibold text-[#180D24] leading-snug mb-2 line-clamp-2">{d.title}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={TYPE_STYLES}>{d.record_type}</span>
              {d.year && <span className="text-xs text-[#756D7E] font-mono">{d.year}</span>}
              {d.scanned && <span className="text-[10px] font-mono text-[#047857] flex items-center gap-0.5"><ScanText size={11} /> Auto-read</span>}
            </div>
            {d.ancestor && <div className="mt-3 text-xs text-[#756D7E]">Linked: <span className="text-[#180D24] font-medium">{d.ancestor}</span></div>}
          </Panel>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#180D24] pr-8">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <EvidenceBadge status={selected.evidence_status} size="lg" />
                <span className={TYPE_STYLES}>{selected.record_type}</span>
                {selected.source_type && <span className={TYPE_STYLES}>{selected.source_type}</span>}
              </div>
              {selected.extracted_text && (
                <div className="mt-4">
                  <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1">Transcription</div>
                  <p className="text-sm text-[#1A171D] leading-relaxed bg-[#FAF8F5] border border-[#E4DCD3] rounded-xl p-4">{selected.extracted_text}</p>
                </div>
              )}
              {selected.citation && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1">Citation</div>
                  <p className="text-sm font-mono text-[#59288E] bg-[#F3EEFA] rounded-lg p-3">{selected.citation}</p>
                </div>
              )}
              <button
                data-testid="vault-analyze-btn"
                onClick={() => { setSelected(null); onNavigate("record_reader"); }}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#59288E] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors"
              >
                <ScanText size={16} /> Analyze with AI Record Reader
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploading) setUploadOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#180D24]">Upload a Document</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#756D7E] -mt-1">Add a JPG, PNG or PDF. It will be auto-read by AI so you can extract names, dates and places.</p>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Title</label>
              <input
                data-testid="upload-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. 1900 Census — Johnson Family"
                className="mt-1 w-full rounded-xl border border-[#E4DCD3] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
              />
            </div>

            <div>
              <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Document Type</label>
              <select
                data-testid="upload-type-select"
                value={form.record_type}
                onChange={(e) => setForm({ ...form, record_type: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">File (JPG, PNG or PDF)</label>
              <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={pickFile} data-testid="vault-file-input" />
              {!file ? (
                <button
                  data-testid="upload-choose-file-btn"
                  onClick={() => fileRef.current?.click()}
                  className="mt-1 w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#D4AF37]/50 bg-[#FFF9E6] py-6 text-sm text-[#8F7220] hover:border-[#D4AF37] transition-colors"
                >
                  <UploadCloud size={22} />
                  Choose a JPG, PNG or PDF
                </button>
              ) : (
                <div className="mt-1 flex items-center gap-3 rounded-xl border border-[#E4DCD3] bg-[#FAF8F5] px-3 py-2.5" data-testid="upload-file-chip">
                  {file.type === "application/pdf" ? <FileText size={18} className="text-[#59288E]" /> : <ImageIcon size={18} className="text-[#59288E]" />}
                  <span className="flex-1 text-sm text-[#180D24] truncate">{file.name}</span>
                  <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#9CA3AF] hover:text-[#B91C1C]" data-testid="upload-file-clear">
                    <X size={15} />
                  </button>
                </div>
              )}
            </div>

            <button
              data-testid="upload-submit-btn"
              onClick={submitUpload}
              disabled={uploading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] py-3 text-sm font-semibold hover:bg-[#C59B27] transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
              {uploading ? "Uploading & reading…" : "Upload to Vault"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
